import { useId, useState } from "react";
import type { MedicationIntake, MedicationSetting } from "@/domain/entities";
import { canonicalMedicineName, suggestMedicines } from "@/domain/medicineSuggestions";
import { nativeDatalistWorks } from "@/app/platform/nativeSuggestions";
import { Button } from "../Button";
import { useLocale } from "../../i18n/LocaleProvider";
import { SubmitRow } from "./SubmitRow";
import styles from "./questions.module.css";

interface Props {
  value: MedicationIntake[] | undefined;
  medicines: MedicationSetting[];
  optional: boolean;
  onSubmit: (value: MedicationIntake[]) => void;
}

const HELPED: NonNullable<MedicationIntake["helped"]>[] = ["yes", "partly", "no"];

export function MedicationInput({ value, medicines, optional, onSubmit }: Props) {
  const { t } = useLocale();
  const listId = useId();
  // A required group opens with one empty row so the user can start typing straight away.
  const [intakes, setIntakes] = useState<MedicationIntake[]>(value ?? (optional ? [] : [{ name: "", count: 1 }]));

  const patch = (index: number, change: Partial<MedicationIntake>) =>
    setIntakes((prev) => prev.map((intake, i) => (i === index ? { ...intake, ...change } : intake)));

  // The browser's own suggestions where they work; the app's chips where they do not (see nativeSuggestions.ts).
  const native = nativeDatalistWorks(navigator.userAgent);

  /** A typed name that matches a known medicine takes its stored spelling and, once, its usual dose. */
  const setName = (index: number, typed: string) => {
    const name = canonicalMedicineName(medicines, typed);
    const known = medicines.find((m) => m.name === name);
    patch(index, known?.defaultDose && !intakes[index]?.dose ? { name, dose: known.defaultDose } : { name });
  };

  const helpedLabel = { yes: t.questionnaire.helpedYes, partly: t.questionnaire.helpedPartly, no: t.questionnaire.helpedNo };

  return (
    <div className={styles.stack}>
      {intakes.map((intake, index) => {
        const suggestions = suggestMedicines(medicines, intake.name);
        const rowListId = `${listId}-${index}`;
        return (
        <div key={index} className={styles.intake}>
          <div className={styles.intakeRow}>
            <label>
              {t.questionnaire.medicine}
              <input
                className={styles.field}
                list={native ? rowListId : undefined}
                value={intake.name}
                onChange={(e) => setName(index, e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="words"
                spellCheck={false}
              />
              {native && (
                <datalist id={rowListId}>
                  {suggestions.map((m) => <option key={m.name} value={m.name} />)}
                </datalist>
              )}
            </label>
            <label>
              {t.questionnaire.dose}
              <input className={styles.field} value={intake.dose ?? ""} onChange={(e) => patch(index, { dose: e.target.value })} />
            </label>
          </div>
          {!native && suggestions.length > 0 && (
            <div className={styles.suggestions} data-testid="suggestions">
              {suggestions.map((m) => (
                <button key={m.name} type="button" className={styles.suggestion} onClick={() => setName(index, m.name)}>
                  {m.name}
                </button>
              ))}
            </div>
          )}
          <div className={styles.intakeRow}>
            <span>{t.questionnaire.times}</span>
            <div className={styles.counter}>
              <Button aria-label="-" onClick={() => patch(index, { count: Math.max(1, intake.count - 1) })}>−</Button>
              <span>{intake.count}</span>
              <Button aria-label="+" onClick={() => patch(index, { count: intake.count + 1 })}>+</Button>
            </div>
            <Button variant="danger" onClick={() => setIntakes((prev) => prev.filter((_, i) => i !== index))}>{t.common.remove}</Button>
          </div>
          <div className={styles.intakeRow}>
            <span>{t.questionnaire.helped}</span>
            {HELPED.map((helped) => (
              <Button key={helped} selected={intake.helped === helped} onClick={() => patch(index, { helped })}>
                {helpedLabel[helped]}
              </Button>
            ))}
          </div>
        </div>
        );
      })}
      <div className={styles.row}>
        <Button onClick={() => setIntakes((prev) => [...prev, { name: "", count: 1 }])}>{t.questionnaire.addMedicine}</Button>
        {optional && <Button variant="ghost" onClick={() => onSubmit([])}>{t.questionnaire.nothingTaken}</Button>}
      </div>
      <SubmitRow onNext={() => onSubmit(intakes.map((i) => ({ ...i, name: i.name.trim(), dose: i.dose?.trim() || undefined })))} />
    </div>
  );
}
